import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
});

// Request interceptor for token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        await axios.post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true });
        return api(originalRequest);
      } catch (refreshError) {
        // Come back to the same screen after login (e.g. a customer scanned
        // with the phone camera: /s/LP-…), not to the dashboard.
        const here = window.location.pathname + window.location.search;
        window.location.href = here.startsWith('/login') ? '/login' : `/login?redirect=${encodeURIComponent(here)}`;
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  me: () => api.get('/auth/me'),
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  logout: () => api.post('/auth/logout'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, password) => api.post('/auth/reset-password', { token, new_password: password }),
};

// Cards
export const cardsAPI = {
  list: () => api.get('/cards'),
  get: (id) => api.get(`/cards/${id}`),
  create: (data) => api.post('/cards', data),
  update: (id, data) => api.put(`/cards/${id}`, data),
  delete: (id) => api.delete(`/cards/${id}`),
};

// Customers
export const customersAPI = {
  list: (params) => api.get('/customers', { params }),
  get: (id) => api.get(`/customers/${id}`),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`),
  walletStatus: (id) => api.get(`/customers/${id}/wallet-status`),
};

// Transactions
export const transactionsAPI = {
  list: (params) => api.get('/transactions', { params }),
  create: (data) => api.post('/transactions', data),
  scan: (customerQr, cardId) => api.post('/transactions/scan', { customer_qr: customerQr, card_id: cardId }),
};

// Geofences
export const geofencesAPI = {
  list: (cardId) => api.get('/geofences', { params: { card_id: cardId } }),
  create: (data) => api.post('/geofences', data),
  update: (id, data) => api.put(`/geofences/${id}`, data),
  delete: (id) => api.delete(`/geofences/${id}`),
  geocode: (address) => api.post('/geofences/geocode', { address }),
};

// Notifications
export const notificationsAPI = {
  list: (limit) => api.get('/notifications', { params: { limit } }),
  create: (data) => api.post('/notifications', data),
  resend: (id) => api.post(`/notifications/${id}/resend`),
  previewCount: (data) => api.post('/notifications/preview-count', data),
  test: (data) => api.post('/notifications/test', data),
  pushPromo: (data) => api.post('/notifications/push-promo', data),
  getQuota: () => api.get('/notifications/quota'),
};

// Analytics
export const analyticsAPI = {
  dashboard: () => api.get('/analytics/dashboard'),
  card: (cardId) => api.get(`/analytics/card/${cardId}`),
};

// Billing
export const billingAPI = {
  plans: () => api.get('/billing/plans'),
  subscription: () => api.get('/billing/subscription'),
  checkout: (planId, originUrl) => api.post('/billing/checkout', { plan_id: planId, origin_url: originUrl }),
  status: (sessionId) => api.get(`/billing/status/${sessionId}`),
};

// Files
export const filesAPI = {
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  get: (fileId) => api.get(`/files/${fileId}`, { responseType: 'blob' }),
};

// Wallet Templates
export const walletAPI = {
  appleTemplate: (cardId) => api.get(`/wallet/apple/template/${cardId}`),
  googleTemplate: (cardId) => api.get(`/wallet/google/template/${cardId}`),
  applePass: (cardId, customerId) => api.get(`/wallet/apple/pass/${cardId}/${customerId}`, { responseType: 'blob' }),
  googleSaveLink: (cardId, customerId) => api.get(`/wallet/google/save-link/${cardId}/${customerId}`),
};

// Scanner
export const scannerAPI = {
  lookup: (memberCode, mid) => api.get(`/scan/lookup/${memberCode}`, mid ? { params: { mid } } : undefined),
  quick: (payload) => api.post('/scan/quick', payload),
  undo: (transactionId) => api.post(`/scan/undo/${transactionId}`),
};

// Card Templates & Presets
export const designAPI = {
  templates: () => api.get('/cards/templates'),
  colorPresets: () => api.get('/cards/color-presets'),
  uploadLogo: (cardId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/cards/${cardId}/upload-logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export default api;
